import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import { FiTool, FiAlertTriangle, FiClock, FiMail, FiTwitter, FiRefreshCw, FiCheckCircle } from 'react-icons/fi';
import './MaintenanceMode.css';

const MaintenanceMode = () => {
  const { 
    isMaintenanceMode, 
    getMaintenanceMessage, 
    getMaintenanceProgress,
    getMaintenanceETA,
    getMaintenanceType,
    getMaintenanceContact
  } = useWallet();
  
  const [lastCheck, setLastCheck] = useState(new Date());
  const [isChecking, setIsChecking] = useState(false);
  const [localProgress, setLocalProgress] = useState(0);

  // Get maintenance data
  const maintenanceData = {
    message: getMaintenanceMessage(),
    progress: getMaintenanceProgress(),
    eta: getMaintenanceETA(),
    type: getMaintenanceType(),
    contact: getMaintenanceContact()
  };

  // Auto-refresh maintenance status every 30 seconds
  useEffect(() => {
    if (!isMaintenanceMode()) return;

    const interval = setInterval(() => {
      setLastCheck(new Date());
      // Trigger a check for maintenance status
      window.location.reload();
    }, 30000);

    return () => clearInterval(interval);
  }, [isMaintenanceMode]);

  // Simulate progress animation (in real implementation, this would come from admin panel)
  useEffect(() => {
    if (!isMaintenanceMode()) return;

    const progressInterval = setInterval(() => {
      setLocalProgress(prev => {
        const targetProgress = maintenanceData.progress;
        if (prev >= targetProgress) return prev;
        return Math.min(prev + 1, targetProgress);
      });
    }, 2000);

    return () => clearInterval(progressInterval);
  }, [isMaintenanceMode, maintenanceData.progress]);

  // Manual refresh function
  const handleRefresh = () => {
    setIsChecking(true);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Format ETA for display
  const formatETA = (eta) => {
    if (!eta) return null;
    try {
      const etaDate = new Date(eta);
      const now = new Date();
      const diff = etaDate - now;
      
      if (diff <= 0) return 'Completed';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m remaining`;
      } else {
        return `${minutes}m remaining`;
      }
    } catch (error) {
      return eta;
    }
  };

  // Get maintenance type display text
  const getMaintenanceTypeText = (type) => {
    const types = {
      scheduled: 'Scheduled Maintenance',
      emergency: 'Emergency Maintenance',
      upgrade: 'System Upgrade',
      security: 'Security Update'
    };
    return types[type] || 'Maintenance';
  };

  if (!isMaintenanceMode()) {
    return null;
  }

  return (
    <div className="maintenance-mode-overlay">
      <div className="maintenance-mode-content">
        <div className="maintenance-card">
          <div className="maintenance-header">
            <FiTool className="maintenance-icon" />
            <h2>Maintenance Mode</h2>
          </div>
          
          <div className="maintenance-body">
            <div className="maintenance-spinner">
              <FiTool className="spinning-icon" />
            </div>
            
            <h3>{getMaintenanceTypeText(maintenanceData.type)}</h3>
            
            <p className="maintenance-message">
              {maintenanceData.message}
            </p>

            {/* Progress Section */}
            <div className="maintenance-progress">
              <div className="progress-header">
                <FiClock className="progress-icon" />
                <span>Maintenance Progress</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${localProgress}%` }}
                ></div>
              </div>
              <div className="progress-text">
                {localProgress.toFixed(1)}% Complete
              </div>
            </div>

            {/* Estimated Time */}
            {maintenanceData.eta && (
              <div className="estimated-time">
                <FiClock className="time-icon" />
                <span>Estimated completion: {formatETA(maintenanceData.eta)}</span>
              </div>
            )}

            {/* Last Check */}
            <div className="last-check">
              <span>Last checked: {lastCheck.toLocaleTimeString()}</span>
            </div>

            {/* Action Buttons */}
            <div className="maintenance-actions">
              <button 
                className="refresh-button"
                onClick={handleRefresh}
                disabled={isChecking}
              >
                <FiRefreshCw className={`refresh-icon ${isChecking ? 'spinning' : ''}`} />
                {isChecking ? 'Checking...' : 'Check Status'}
              </button>
            </div>

            {/* Contact Information */}
            {(maintenanceData.contact.email || maintenanceData.contact.twitter) && (
              <div className="maintenance-contact">
                <h4>Need Help?</h4>
                <div className="contact-links">
                  {maintenanceData.contact.email && (
                    <a href={`mailto:${maintenanceData.contact.email}`} className="contact-link">
                      <FiMail className="contact-icon" />
                      <span>Email Support</span>
                    </a>
                  )}
                  {maintenanceData.contact.twitter && (
                    <a href={maintenanceData.contact.twitter} target="_blank" rel="noopener noreferrer" className="contact-link">
                      <FiTwitter className="contact-icon" />
                      <span>Follow Updates</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="maintenance-footer">
              <FiAlertTriangle className="warning-icon" />
              <span>Please check back later. We apologize for any inconvenience.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMode; 